import { gql } from '@apollo/client'
import moment from 'moment'

import {
    DEPARTMENT,
    EMPLOYEE,
    OFFICE,
    PERSONIO,
    POSITION,
    RESERVABLE,
    SPACE,
    SUBSCRIPTION_PLAN,
    SUBSCRIBED_BILLING,
    TRIAL_BILLING,
    SUBSCRIPTION_FOR_COMPANY,
    RULES,
    SLACK,
    BAMBOOHR,
    CALENDAR_DATA,
    MAIN_EMPLOYEE,
} from './dataQuery'

const currentMoment = moment()

export const QUERY_GET_COMPANY = gql`
    query GetCompany($id: ID!, $employeeId: ID!) {
        getCompany(id: $id) {
            id
            title
            country
            city
            address
            zipcode
            phoneNumber
            numberOfOffices
            numberOfEmployees
            companyLogo
            tariff
            Rules {
                ${RULES}
            }
            Offices {
                items {
                    id
                    name
                }
            }
            calendarIntegration
        }
        getEmployee(id: $employeeId) {
            id
            firstname
            lastname
            cognitoUserId
            email
            photo
            role
            favouriteOfficeID
            favouriteColleagueIDs
            favouriteReservableID
            locale
            departmentIDs
            departmentsString
            Position {
                id
                name
            }
            Office {
                id
                name
            }
            notificationChannels
            shareAttendance
            demoAccountID
        }
    }
`

export const QUERY_LIST_EMPLOYEES = gql`
    query ListEmployees($companyID: ID!, $nextToken: String) {
        listEmployeesByCompanyAndEmail(limit: 50, companyID: $companyID, sortDirection: ASC, nextToken: $nextToken) {
            items {
                ${EMPLOYEE}
                Office {
                    ${OFFICE}
                }
                Position {
                    ${POSITION}
                }
            }
            nextToken
        }
    }
`

export const QUERY_LIST_EMPLOYEES_BY_COMPANY_AND_FIRSTNAME = gql`
    query GetEmployeesByCompanyAndStatusAndFirstname($companyID: ID!) {
        listEmployeesByCompanyAndFirstname(companyID: $companyID, limit: 1000) {
            items {
                id
                firstname
                lastname
                photo
            }
        }
    }
`

export const QUERY_GET_TRACING_CSV = gql`
    query GetContactTracingCsv($employeeID: ID!, $date: AWSDate!) {
        getContactTracingCsv(employeeID: $employeeID, date: $date)
    }
`

export const QUERY_GET_S3_LINK = gql`
    query GetS3Link($operationType: S3OperationType!, $path: String!, $resize: ImageResizeInput) {
        getS3SignedLink(operationType: $operationType, path: $path, resize: $resize)
    }
`

export const QUERY_LIST_RESERVABLES_BY_TYPE = gql`
    query ListReservablesByCompanyAndType($companyID: ID!, $type: String!) {
        listReservablesByCompanyAndType(companyID: $companyID, type: { eq: $type }) {
            items {
                id
                type
            }
        }
    }
`

export const QUERY_LIST_SPACES_ALL = gql`
    query ListSpaces($companyID: ID!) {
        listSpacesByCompanyAndName(companyID: $companyID, sortDirection: ASC) {
            items {
                ${SPACE}
                Office {
                    ${OFFICE}
                }
                Reservables(limit: 1000) {
                    items {
                        ${RESERVABLE}
                        Employee {
                            ${EMPLOYEE}
                            Position {
                                ${POSITION}
                            }
                        }
                        Department {
                            name
                        }
                    }
                }
            }
        }
    }
`

export const QUERY_LIST_DEPARTMENTS = gql`
    query ListDepartments($companyID: ID!) {
        listDepartmentsByCompanyAndName(companyID: $companyID, sortDirection: ASC) {
            items {
                ${DEPARTMENT}
            }
        }
    }
`

export const QUERY_LIST_POSITIONS = gql`
    query ListPositions($companyID: ID!) {
        listPositionsByCompanyAndName(companyID: $companyID, sortDirection: ASC, limit: 1000) {
            items {
                ${POSITION}
            }
        }
    }
`

export const QUERY_CHECKOUT_PAYLINK_URL = gql`
    query GetPaylinkUrlForCheckout($planID: String!, $returnUrl: String!) {
        getPaylinkUrlForCheckout(planID: $planID, returnUrl: $returnUrl) {
            url
        }
    }
`

const SUBSCRIBED_BILLING_FRAGMENT = gql`
    fragment SubscribedBillingFields on SubscribedBilling {
        ${SUBSCRIBED_BILLING}
    }
`

const TRIAL_BILLING_FRAGMENT = gql`
    fragment TrialBillingFields on TrialBilling {
        ${TRIAL_BILLING}
    }
`

export const QUERY_GET_SUBSCRIPTIONS_FOR_COMPANY = gql`
    ${SUBSCRIBED_BILLING_FRAGMENT}
    ${TRIAL_BILLING_FRAGMENT}
    query GetSubscriptionsForCompany {
        getSubscriptionsForCompany {
            items {
                ${SUBSCRIPTION_FOR_COMPANY}
                ...SubscribedBillingFields
                ...TrialBillingFields
            }
        }
    }
`

export const QUERY_LIST_PAYMENTS = gql`
    query ListPayments($subscriptionId: String!) {
        listPayments(subscriptionId: $subscriptionId) {
            id
            subscriptionId
            amount
            currency
            payoutDate
            invoiceUrl
        }
    }
`

export const QUERY_LIST_SUBSCRIPTIONS_PLANS = gql`
    query ListSubscriptionsPlans {
        listSubscriptionsPlans {
            plans {
               ${SUBSCRIPTION_PLAN}
            }
        }
    }
`

export const QUERY_GET_PERSONIO_INTEGRATION = gql`
    query GetPersonioIntegration {
        getPersonioIntegration {
            ${PERSONIO}
        }
    }
`
export const QUERY_GET_SLACK_INTEGRATION = gql`
    query GetSlackIntegration {
        getSlackIntegration {
            ${SLACK}
        }
    }
`

export const QUERY_LIST_NOTIFCATION_INTEGRATIONS = gql`
    query ListNotificationIntegrations {
        listNotificationIntegrations {
            type
            group
            status
        }
    }
`

export const QUERY_GET_SLACK_INSTALL_URL = gql`
    query GetSlackIntegrationInstallUrl {
        getSlackIntegrationInstallUrl
    }
`

export const QUERY_GET_BAMBOOHR_INTEGRATION = gql`
    query GetBambooHRIntegration {
        getBambooHRIntegration {
            ${BAMBOOHR}
        }
    }
`

export const QUERY_GET_CALENDAR_SEARCH_EMPLOYEES = gql`
    query GetCalendarEmployees($weekNumber: Int!, $year: Int!, $filter: SearchableEmployeeFilterInput, $limit: Int, $nextToken: String) {
        employees: searchEmployees(filter: $filter, limit: $limit, nextToken: $nextToken, sort: { field: fullNameLowerCase, direction: asc }) {
            items {
                companyID
                departmentIDs
                departmentsString
                firstname
                lastname
                fullName
                photo
                status
                ${CALENDAR_DATA}
            }
            nextToken
            total
        }
    }
`

export const QUERY_GET_CALENDAR_SEARCH_FAVORITES_EMPLOYEES = gql`
    query GetCalendarFavoriteEmployees($weekNumber: Int!, $year: Int!, $filter: SearchableEmployeeFilterInput, $limit: Int, $nextToken: String) {
        employees: searchEmployees(filter: $filter, limit: $limit, nextToken: $nextToken, sort: { field: fullNameLowerCase, direction: asc }) {
            items {
                companyID
                departmentIDs
                departmentsString
                firstname
                lastname
                fullName
                photo
                status
                ${CALENDAR_DATA}
            }
            nextToken
            total
        }
    }
`

export const QUERY_SEARCH_EMPLOYEES = gql`
    query GetEmployeesForBooking(
        $filter: SearchableEmployeeFilterInput
        $limit: Int
        $nextToken: String
        $date: AWSDate!
    ) {
        searchEmployees(
            filter: $filter
            limit: $limit
            nextToken: $nextToken
            sort: { field: fullNameLowerCase, direction: asc }
        ) {
            items {
                id
                companyID
                departmentIDs
                firstname
                lastname
                fullName
                photo
                Position {
                    name
                }
                isSeatingInOffice(date: $date)
                status
            }
            nextToken
            total
        }
    }
`
export const QUERY_SEARCH_SPACES_FOR_EMPLOYEES = gql`
    query GetSpacesForEmployees($employeeIds: [ID!], $date: AWSDate!, $companyId: ID!) {
        listSpacesByCompanyAndName(companyID: $companyId, limit: 100) {
            items {
                id
                name
                totalSeats
                isAvailableToEmployees(date: $date, employeeIDs: $employeeIds)
            }
            nextToken
        }
    }
`

export const QUERY_SEAT_BOOK_WEEK_AGGREAGTION_BY_SPACE = gql`
    query ListSpaceWeekAggregationBySpaceAndYearAndWeekNumber($spaceID: ID!, $weekNumber: Int, $year: Int) {
        listSpaceWeekAggregationBySpaceAndYearAndWeekNumber(
            spaceID: $spaceID
            yearWeekNumber: { beginsWith: { weekNumber: $weekNumber, year: $year } }
        ) {
            items {
                weekNumber
                year
                dayAggregations(
                    yearWeekNumberDate: { beginsWith: { weekNumber: $weekNumber, year: $year } }
                    filter: { weekNumber: { eq: $weekNumber } }
                ) {
                    items {
                        date
                        spaceCapacityLast
                        spaceBookingsSum
                        waitingListItemsSum
                        homeBookingsSum
                    }
                }
            }
        }
    }
`

export const QUERY_SEAT_BOOK_WEEK_AGGREAGTION_BY_OFFICE = gql`
    query ListOfficeWeekAggregationByOfficeAndYearAndWeekNumber($officeID: ID!, $weekNumber: Int, $year: Int) {
        listOfficeWeekAggregationByOfficeAndYearAndWeekNumber(
            officeID: $officeID
            yearWeekNumber: { beginsWith: { weekNumber: $weekNumber, year: $year } }
        ) {
            items {
                weekNumber
                year
                dayAggregations(
                    yearWeekNumberDate: { beginsWith: { weekNumber: $weekNumber, year: $year } }
                    filter: { weekNumber: { eq: $weekNumber } }
                ) {
                    items {
                        date
                        officeCapacityLast
                        officeBookingsSum
                        waitingListItemsSum
                        homeBookingsSum
                    }
                }
            }
        }
    }
`

export const QUERY_SEAT_BOOK_WEEK_AGGREAGTION_BY_ALL = gql`
    query ListCompanyWeekAggregationByCompanySeatsBook($companyID: ID!, $weekNumber: Int, $year: Int) {
        listCompanyWeekAggregationByCompany(
            companyID: $companyID
            yearWeekNumber: { beginsWith: { weekNumber: $weekNumber, year: $year } }
        ) {
            items {
                weekNumber
                year
                dayAggregations(
                    yearWeekNumberDate: { beginsWith: { weekNumber: $weekNumber, year: $year } }
                    filter: { weekNumber: { eq: $weekNumber } }
                ) {
                    items {
                        date
                        officeCapacityLast
                        officeBookingsSum
                        waitingListItemsSum
                        homeBookingsSum
                    }
                }
            }
        }
    }
`

export const QUERY_OFFICE_DAY_BY_WEEK_AGGREGATION = gql`
    query ListCompanyWeekAggregationByCompanyAndMoment($companyID: ID!, $weekNumberString: String, $year: Int) {
        listCompanyWeekAggregationByCompanyAndMoment(
            companyID: $companyID
            yearWeekNumberString: {
                between: [{ weekNumberString: $weekNumberString, year: $year }, { weekNumberString: "${currentMoment.format(
                    'ww',
                )}", year: ${currentMoment.year()} }]
            }      
        ) {
            items {
                weekNumber
                year
                officeCapacityMax
                officeBookingsSum
                bookingsDistribution {
                    amountOfDays
                    bookingsSum
                }
            }
        }
    }
`

export const QUERY_HOME_OFFICE_COMPANY_WEEK_AGGREGATION = gql`
    query ListCompanyWeekAggregationByCompany($companyID: ID!, $weekNumber: Int, $year: Int) {
        listCompanyWeekAggregationByCompany(
            companyID: $companyID
            yearWeekNumber: { beginsWith: { weekNumber: $weekNumber, year: $year } }
        ) {
            items {
                weekNumber
                year
                dayAggregations(
                    yearWeekNumberDate: { beginsWith: { weekNumber: $weekNumber, year: $year } }
                    filter: { weekNumber: { eq: $weekNumber } }
                ) {
                    items {
                        date
                        officeCapacityLast
                        officeBookingsSum
                        waitingListItemsSum
                        homeBookingsSum
                    }
                }
            }
        }
    }
`

export const QUERY_UTILIZZATION_WEEK_AGGREAGTION_BY_SPACE = gql`
    query ListSpaceWeekAggregationBySpaceAndMoment($spaceID: ID!, $weekNumberString: String, $year: Int) {
        listSpaceWeekAggregationBySpaceAndMoment(
            spaceID: $spaceID
            yearWeekNumberString: {
                between: [{ weekNumberString: $weekNumberString, year: $year }, { weekNumberString: "${currentMoment.format(
                    'ww',
                )}", year: ${currentMoment.year()} }]
            }
        ) {
            items {
                weekNumber
                year
                spaceCapacityMax
                spaceBookingsSum
            }
        }
    }
`

export const QUERY_UTILIZZATION_WEEK_AGGREAGTION_BY_ALL = gql`
    query ListCompanyWeekAggregationByCompanyAndMoment($companyID: ID!, $weekNumberString: String, $year: Int) {
        listCompanyWeekAggregationByCompanyAndMoment(
            companyID: $companyID
            yearWeekNumberString: {
                between: [{ weekNumberString: $weekNumberString, year: $year }, { weekNumberString: "${currentMoment.format(
                    'ww',
                )}", year: ${currentMoment.year()} }]
            }
        ) {
            items {
                weekNumber
                year
                officeCapacityMax
                officeBookingsSum
                bookingsDistribution {
                    amountOfDays
                    bookingsSum
                }
            }
        }
    }
`

export const QUERY_GET_SPACES = gql`
    query GetSpacesWithReservablesAndCalendarEvents($companyId: ID!, $type: ReservableType, $date: String!) {
        listSpacesByCompanyAndName(companyID: $companyId, sortDirection: ASC) {
            items {
                id
                name
                officeID
                workingHoursFrom
                workingHoursTo
                Reservables(filter: { type: { eq: $type } }, limit: 1000) {
                    items {
                        id
                        name
                        maxAmountOfPeople
                        ReservableToDepartments {
                            items {
                                Department {
                                    id
                                }
                            }
                        }
                        Tags {
                            items {
                                Tag {
                                    name
                                }
                            }
                        }
                        availabilityType
                        externalRoomID
                        CalendarEvents(startTime: { beginsWith: $date }) {
                            items {
                                id
                                title
                                description
                                startTime
                                endTime
                                canCurrentUserEdit
                                parentEventExternalId
                                isOnlineMeeting
                                onlineMeetingUrl
                                isPrivate
                                employeesIDs
                                organiserEmployeeID
                            }
                        }
                    }
                }
            }
        }
    }
`

export const QUERY_GET_EVENTS_FOR_ROOMS = gql`
    query GetEventsForRooms($reservableId: ID!, $date: String!) {
        listCalendarEventsByReservableAndStartTime(reservableID: $reservableId, startTime: { beginsWith: $date }) {
            items {
                id
                title
                description
                startTime
                endTime
                canCurrentUserEdit
                parentEventExternalId
                isOnlineMeeting
                onlineMeetingUrl
                isPrivate
            }
        }
    }
`

export const QUERY_GET_ATTENDEES_FOR_EVENT = gql`
    query GetAttendeesForEvent($eventId: ID!) {
        getCalendarEvents(id: $eventId) {
            Employees {
                items {
                    Employee {
                        id
                        fullName
                        photo
                        isSeatingInOffice
                    }
                }
            }
            OrganiserEmployee {
                id
                fullName
                photo
                isSeatingInOffice
            }
        }
    }
`

export const QUERY_GET_WAITINGLIST_SPACE_SEATS_BY_ID = gql`
    query GetWaitingListSpace($spaceID: ID! , $date: String!) {
        getSpace(id: $spaceID) {
            name
            floorPlanFilePath
            Reservables(filter: { type: { eq: SEAT } }) {
                items {
                    ${RESERVABLE}
                    Bookings(startTime: { beginsWith: $date }) {
                        items {
                            id
                            Employee {
                               ${MAIN_EMPLOYEE}
                            }
                        }
                    }
                }
            }
        }
    }
`

export const QUERY_GET_LIST_OFFICES_BY_COMPANY = gql`
    query GetListOfficesByCompany($companyId: ID!) {
        listOfficesByCompanyAndOrder(companyID: $companyId) {
            items {
                id
                name
                workingFrom
                workingTo
                address
                timeZone
                externalOfficeId
                SpaceRows {
                    items {
                        id
                        Spaces {
                            items {
                                id
                                floorPlanFilePath
                                name
                                totalSeats
                                totalRooms
                                totalParkingSlots
                            }
                        }
                    }
                }
            }
        }
    }
`
export const QUERY_CHECK_SEAT_AVAILABLE = gql`
    query ListReservablesWithBookingsBySpace(
        $employee: [EmployeesWithDepartments]
        $dates: [AWSDate]!
        $bookingRepeatType: RepeatType
        $daysOfWeek: [String]
        $startTime: String
        $endTime: String
        $dateFilter: ModelBookingByReservableAndDateAndDayOfWeekCompositeKeyConditionInput
        $filter: ModelBookingFilterInput
        $reservableId: ID!
    ) {
        getReservable(id: $reservableId) {
            id
            availabilityByDatesAndTime(
                bookingRepeatType: $bookingRepeatType
                employees: $employee
                dates: $dates
                daysOfWeek: $daysOfWeek
                startTime: $startTime
                endTime: $endTime
            )
            BookingsByDateAndDayOfWeek(
                limit: 1000,
                dateDayOfWeek: $dateFilter
                filter: $filter
            ) {
                items {
                    id
                    startTime
                    endTime
                    isFullDay
                    isBlocked
                    employeeID
                    Employee {
                        ${MAIN_EMPLOYEE}
                    }
                }
            }
        }
    }
`
export const GET_RESERVABLE_BY_ID = gql`
    query getReservableById(
        $employee: [EmployeesWithDepartments]
        $dates: [AWSDate]
        $bookingRepeatType: RepeatType
        $daysOfWeek: [String]
        $startTime: String
        $endTime: String
        $dateFilter: ModelBookingByReservableAndDateAndDayOfWeekCompositeKeyConditionInput
        $filter: ModelBookingFilterInput
        $reservableId: ID!
    ) {
        getReservable(id: $reservableId) {
            id
            name
            x
            y
            type
            availabilityType
            maxAmountOfPeople
            zonePoints
            color
            Employee {
                ${MAIN_EMPLOYEE}
            }
            Space {
                name
                id
                address
            }

            Department {
                name
            }

            Tags {
                items {
                    Tag {
                        id
                        name
                    }
                }
            }

            ReservableToDepartments {
                items {
                    Department {
                        id
                        name
                    }
                }
            }

            availabilityByDatesAndTime(
                bookingRepeatType: $bookingRepeatType,
                employees: $employee,
                dates: $dates
                daysOfWeek: $daysOfWeek
                startTime: $startTime
                endTime: $endTime
            )

            BookingsByDateAndDayOfWeek(
                limit: 1000,
                dateDayOfWeek: $dateFilter
                filter: $filter
            ) {
                items {
                    id
                    startTime
                    endTime
                    isFullDay
                    isBlocked
                    employeeID
                    Employee {
                        ${MAIN_EMPLOYEE}
                    }
                }
            }
        }
    }
`
